import React, { useState } from 'react';
import {
  Box, Typography, TextField, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Chip, CircularProgress,
  Alert, InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import accountsApiService from '../../services/accountsApiService';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/reactApi/api';

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const [year, month, day] = dateString.split('-');
  return `${day}/${month}/${year}`;
};

const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return '₹ 0.00';
  return `₹ ${Number(amount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const Ledger = () => {
  const [customerNo, setCustomerNo] = useState('');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searched, setSearched] = useState(false);
  const [downloadingInvoice, setDownloadingInvoice] = useState(null);
  const [downloadingReceipt, setDownloadingReceipt] = useState(null);

  const handleSearch = async () => {
    if (!customerNo.trim()) {
      setError('Enter a username / customer number');
      return;
    }
    try {
      setLoading(true);
      setError(null);
      setSearched(false);
      const response = await accountsApiService.getLedgerEntries(customerNo.trim());
      setData(response.value || []);
      setSearched(true);
    } catch (err) {
      setError(err.message || 'Failed to fetch ledger entries');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadInvoice = async (documentNo) => {
    try {
      setDownloadingInvoice(documentNo);
      const url = `${API_BASE_URL}/accounts/business-central/invoice?invoiceNo=${encodeURIComponent(documentNo)}`;
      const response = await fetch(url);
      if (!response.ok) throw new Error('Invoice not available');
      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = `Invoice_${documentNo.replace(/[^a-zA-Z0-9]/g, '-')}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(objectUrl);
    } catch (err) {
      alert('Failed to download invoice: ' + err.message);
    } finally {
      setDownloadingInvoice(null);
    }
  };

  const handleDownloadReceipt = async (documentNo) => {
    try {
      setDownloadingReceipt(documentNo);
      const url = `${API_BASE_URL}/accounts/business-central/receipt?documentNo=${encodeURIComponent(documentNo)}`;
      const response = await fetch(url);
      if (!response.ok) throw new Error('Receipt not available');
      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = `Receipt_${documentNo.replace(/[^a-zA-Z0-9]/g, '-')}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(objectUrl);
    } catch (err) {
      alert('Failed to download receipt: ' + err.message);
    } finally {
      setDownloadingReceipt(null);
    }
  };

  const currentBalance = data.length > 0 ? data[data.length - 1].runningBalance : 0;

  const headers = [
    'S.No', 'Document No', 'Posting Date', 'Customer Name', 'Lab ID',
    'Document Type', 'Debit', 'Credit', 'Running Balance',
    'Overdue Days',  'Download',
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ fontWeight: 700, color: '#00024c', mb: 3 }}>
        Ledger Lookup
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center' }}>
        <TextField
          label="Username / Customer No"
          value={customerNo}
          onChange={(e) => setCustomerNo(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          placeholder="e.g. Nabln000060"
          sx={{ width: 320 }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
          }}
        />
        <Button
          variant="contained"
          onClick={handleSearch}
          disabled={loading}
          sx={{ bgcolor: '#00024c', '&:hover': { bgcolor: '#000380' }, px: 4, height: 56 }}
        >
          {loading ? <CircularProgress size={22} color="inherit" /> : 'Search'}
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {searched && data.length === 0 && !loading && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">No ledger entries found for "{customerNo}".</Typography>
        </Paper>
      )}

      {data.length > 0 && (
        <TableContainer component={Paper} elevation={2}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#00024c' }}>
                {headers.map((h) => (
                  <TableCell key={h} sx={{ color: 'white', fontWeight: 600, whiteSpace: 'nowrap' }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((row, index) => {
                const isInvoice = row.documentType === 'Invoice';
                const isReceipt = (row.documentNo || '').startsWith('RV/');
                return (
                  <TableRow key={row.EntryNo} sx={{ bgcolor: index % 2 === 0 ? '#f5f5f5' : 'white' }}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>{row.documentNo}</TableCell>
                    <TableCell>{formatDate(row.postingDate)}</TableCell>
                    <TableCell>{row.customerName}</TableCell>
                    <TableCell>{row.labId}</TableCell>
                    <TableCell>
                      <Chip
                        label={isInvoice ? 'Invoice' : isReceipt ? 'Receipt' : row.documentType || '—'}
                        size="small"
                        color={isInvoice ? 'primary' : isReceipt ? 'success' : 'default'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell sx={{ color: row.debitAmount > 0 ? '#d32f2f' : 'inherit' }}>
                      {row.debitAmount > 0 ? formatCurrency(row.debitAmount) : '—'}
                    </TableCell>
                    <TableCell sx={{ color: row.creditAmount > 0 ? '#2e7d32' : 'inherit' }}>
                      {row.creditAmount > 0 ? formatCurrency(row.creditAmount) : '—'}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>{formatCurrency(row.runningBalance)}</TableCell>
                    <TableCell>
                      {row.overDueDays > 0 ? (
                        <Chip label={`${row.overDueDays} days`} size="small" color={row.overDueDays > 30 ? 'error' : 'warning'} />
                      ) : '—'}
                    </TableCell>
                    <TableCell>
                      {isInvoice && (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<DownloadIcon />}
                          disabled={downloadingInvoice === row.documentNo}
                          onClick={() => handleDownloadInvoice(row.documentNo)}
                          sx={{ fontSize: '0.7rem', mr: 0.5 }}
                        >
                          {downloadingInvoice === row.documentNo ? 'Loading...' : 'Invoice'}
                        </Button>
                      )}
                      {isReceipt && (
                        <Button
                          size="small"
                          variant="outlined"
                          color="success"
                          startIcon={<DownloadIcon />}
                          disabled={downloadingReceipt === row.documentNo}
                          onClick={() => handleDownloadReceipt(row.documentNo)}
                          sx={{ fontSize: '0.7rem' }}
                        >
                          {downloadingReceipt === row.documentNo ? 'Loading...' : 'Receipt'}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              <TableRow sx={{ bgcolor: '#e3f2fd' }}>
                <TableCell colSpan={8} sx={{ fontWeight: 700, textAlign: 'right', fontSize: '1rem' }}>
                  Current Balance:
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '1.1rem', color: currentBalance > 0 ? '#d32f2f' : '#2e7d32' }}>
                  {formatCurrency(currentBalance)}
                </TableCell>
                <TableCell colSpan={4} />
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default Ledger;